/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useEffect, useCallback } from 'react'
// Third-party
import { CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { css, Global } from '@emotion/react'
import { Modal, Input, Checkbox, Select, TimePicker, App } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import {
  DAYS_OF_WEEK,
  RECURRENCE_TYPES,
  TimeSlot,
  CreateTimeSlotData,
  UpdateTimeSlotData,
} from '../hooks/useSchedulerTimeSlots'

interface TimeRangeInput {
  id?: string
  start_time: Dayjs | null
  end_time: Dayjs | null
}

interface TimeSlotModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateTimeSlotData | UpdateTimeSlotData) => Promise<void>
  facilityName: string
  initialData?: TimeSlot | null
  loading?: boolean
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '24px 16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    padding: '0',
    backgroundColor: colors.SECONDARY_300,
  },
}

const inputStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  color: ${colors.WHITE};
  padding: 10px 12px;
  border-radius: 8px;

  &::placeholder {
    color: ${colors.NEUTRAL_70};
  }

  &:hover,
  &:focus {
    border-color: ${colors.PRIMARY_300};
    background-color: ${colors.SECONDARY_400};
  }

  &.ant-input:focus,
  &.ant-input-focused {
    box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.2);
  }
`

const selectStyles = css`
  width: 100%;

  .ant-select-selector {
    background-color: ${colors.SECONDARY_400} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    color: ${colors.WHITE} !important;
    padding: 4px 12px !important;
    border-radius: 8px !important;
    height: 42px !important;
  }

  .ant-select-selection-item {
    line-height: 32px !important;
    color: ${colors.WHITE} !important;
  }

  &:hover .ant-select-selector {
    border-color: ${colors.PRIMARY_300} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.PRIMARY_300} !important;
    box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.2) !important;
  }

  .ant-select-arrow {
    color: ${colors.NEUTRAL_50};
  }
`

const timePickerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;

  input {
    color: ${colors.WHITE} !important;
  }

  &:hover,
  &.ant-picker-focused {
    border-color: ${colors.PRIMARY_300};
  }

  .ant-picker-suffix {
    color: ${colors.NEUTRAL_50};
  }

  .ant-picker-clear {
    background: ${colors.SECONDARY_400};
    color: ${colors.NEUTRAL_50};
  }
`

// Class name for TimePicker popup to ensure it renders above the modal
const TIME_PICKER_POPUP_CLASS = 'time-slot-modal-time-picker-popup'

const checkboxStyles = css`
  .ant-checkbox-inner {
    background-color: ${colors.SECONDARY_400};
    border-color: ${colors.SECONDARY_200};
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${colors.PRIMARY};
    border-color: ${colors.PRIMARY};
  }

  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${colors.PRIMARY_300};
  }

  span {
    color: ${colors.WHITE};
  }
`

export const TimeSlotModal: React.FC<TimeSlotModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  facilityName,
  initialData,
  loading = false,
}) => {
  const [label, setLabel] = useState('')
  const [recurrenceType, setRecurrenceType] = useState<
    'weekly' | 'biweekly' | 'monthly'
  >('weekly')
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [timeRanges, setTimeRanges] = useState<TimeRangeInput[]>([
    { start_time: null, end_time: null },
  ])
  const { message } = App.useApp()

  const isEdit = !!initialData

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setLabel(initialData.label)
        setRecurrenceType(initialData.recurrence_type)
        setSelectedDays(initialData.days || [])
        setTimeRanges(
          initialData.time_ranges?.map((tr) => ({
            id: tr.id,
            start_time: tr.start_time ? dayjs(tr.start_time, 'HH:mm') : null,
            end_time: tr.end_time ? dayjs(tr.end_time, 'HH:mm') : null,
          })) || [{ start_time: null, end_time: null }]
        )
      } else {
        setLabel('')
        setRecurrenceType('weekly')
        setSelectedDays([])
        setTimeRanges([{ start_time: null, end_time: null }])
      }
    }
  }, [isOpen, initialData])

  const handleDayChange = useCallback((day: number, checked: boolean) => {
    setSelectedDays((prev) =>
      checked
        ? [...prev, day].sort((a, b) => a - b)
        : prev.filter((d) => d !== day)
    )
  }, [])

  const handleAddTimeRange = useCallback(() => {
    setTimeRanges((prev) => [...prev, { start_time: null, end_time: null }])
  }, [])

  const handleRemoveTimeRange = useCallback((index: number) => {
    setTimeRanges((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleTimeRangeChange = useCallback(
    (index: number, field: 'start_time' | 'end_time', value: Dayjs | null) => {
      setTimeRanges((prev) =>
        prev.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr))
      )
    },
    []
  )

  const validateForm = useCallback((): string | null => {
    if (!label.trim()) {
      return 'Please enter a label for this time slot'
    }
    if (selectedDays.length === 0) {
      return 'Please select at least one day of the week'
    }
    const validRanges = timeRanges.filter((tr) => tr.start_time && tr.end_time)
    if (validRanges.length === 0) {
      return 'Please add at least one time range'
    }
    for (const range of validRanges) {
      if (
        range.end_time &&
        range.start_time &&
        range.end_time.isBefore(range.start_time)
      ) {
        return 'End time must be after start time'
      }
    }
    return null
  }, [label, selectedDays, timeRanges])

  const handleSubmit = useCallback(async () => {
    const validationError = validateForm()
    if (validationError) {
      message.error(validationError)
      return
    }

    const formattedTimeRanges = timeRanges
      .filter((tr) => tr.start_time && tr.end_time)
      .map((tr) => ({
        ...(tr.id ? { id: tr.id } : {}),
        start_time: tr.start_time!.format('HH:mm'),
        end_time: tr.end_time!.format('HH:mm'),
      }))

    const data: CreateTimeSlotData | UpdateTimeSlotData = {
      label: label.trim(),
      recurrence_type: recurrenceType,
      days: selectedDays,
      time_ranges: formattedTimeRanges,
    }

    try {
      await onSubmit(data)
      message.success(
        isEdit
          ? 'Time slot updated successfully'
          : 'Time slot added successfully'
      )
      onClose()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save time slot'
      message.error(errorMessage)
    }
  }, [
    validateForm,
    label,
    recurrenceType,
    selectedDays,
    timeRanges,
    onSubmit,
    onClose,
    message,
    isEdit,
  ])

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const footer = (
    <div
      css={css`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 16px;
      `}
    >
      <Button
        text='Cancel'
        onClick={onClose}
        variant='secondary'
        disabled={loading}
      />
      <Button
        text={isEdit ? 'Save Changes' : 'Add Time Slot'}
        onClick={handleSubmit}
        variant='primary'
        isLoading={loading}
        disabled={loading}
      />
    </div>
  )

  return (
    <>
      <Global
        styles={css`
          .${TIME_PICKER_POPUP_CLASS} {
            z-index: 10002 !important;
          }
        `}
      />
      <Modal
        width={560}
        closeIcon={closeIcon}
        title={
          <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
            {isEdit ? 'Edit Time Slot' : `Add Time Slot to ${facilityName}`}
          </Text>
        }
        footer={footer}
        open={isOpen}
        onCancel={onClose}
        centered
        styles={modalStyles}
        maskClosable={false}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 20px;
          `}
        >
          {/* Label */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Label
            </Text>
            <Input
              placeholder='e.g., Evening Sessions, Weekend Games'
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={loading}
              css={inputStyles}
              maxLength={64}
            />
          </div>

          {/* Recurrence Type */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Recurrence
            </Text>
            <Select
              value={recurrenceType}
              onChange={(value) => setRecurrenceType(value)}
              disabled={loading}
              css={selectStyles}
              options={RECURRENCE_TYPES.map((rt) => ({
                value: rt.value,
                label: rt.label,
              }))}
              popupClassName='dark-select-dropdown'
            />
          </div>

          {/* Days of Week */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Days of Week
            </Text>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
              `}
            >
              {DAYS_OF_WEEK.map((day) => (
                <Checkbox
                  key={day.value}
                  checked={selectedDays.includes(day.value)}
                  onChange={(e) => handleDayChange(day.value, e.target.checked)}
                  disabled={loading}
                  css={checkboxStyles}
                >
                  {day.short}
                </Checkbox>
              ))}
            </div>
          </div>

          {/* Time Ranges */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Time Ranges
            </Text>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
              `}
            >
              {timeRanges.map((range, index) => (
                <div
                  key={index}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 12px;
                  `}
                >
                  <TimePicker
                    value={range.start_time}
                    onChange={(value) =>
                      handleTimeRangeChange(index, 'start_time', value)
                    }
                    format='HH:mm'
                    placeholder='Start'
                    disabled={loading}
                    css={timePickerStyles}
                    minuteStep={5}
                    showNow={false}
                    needConfirm={false}
                    popupClassName={TIME_PICKER_POPUP_CLASS}
                  />
                  <span
                    css={css`
                      color: ${colors.NEUTRAL_50};
                    `}
                  >
                    to
                  </span>
                  <TimePicker
                    value={range.end_time}
                    onChange={(value) =>
                      handleTimeRangeChange(index, 'end_time', value)
                    }
                    format='HH:mm'
                    placeholder='End'
                    disabled={loading}
                    css={timePickerStyles}
                    minuteStep={5}
                    showNow={false}
                    needConfirm={false}
                    popupClassName={TIME_PICKER_POPUP_CLASS}
                  />
                  {timeRanges.length > 1 && (
                    <button
                      type='button'
                      onClick={() => handleRemoveTimeRange(index)}
                      disabled={loading}
                      css={css`
                        background: transparent;
                        border: none;
                        color: ${colors.ERROR};
                        cursor: pointer;
                        padding: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        &:hover {
                          color: ${colors.ERROR_LIGHT || '#ff6b6b'};
                        }

                        &:disabled {
                          opacity: 0.5;
                          cursor: not-allowed;
                        }
                      `}
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type='button'
              onClick={handleAddTimeRange}
              disabled={loading}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                background: transparent;
                border: 1px dashed ${colors.SECONDARY_200};
                border-radius: 8px;
                color: ${colors.PRIMARY_300};
                cursor: pointer;
                padding: 10px 16px;
                margin-top: 4px;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;

                &:hover {
                  border-color: ${colors.PRIMARY_300};
                  background: rgba(242, 180, 0, 0.05);
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              <PlusOutlined />
              Add Time Range
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default TimeSlotModal
